<template>
    <SectionBase :section="section">
        <div
            v-for="module in visibleModules()"
            class="col"
            :style="columnSettings(module)"
            :class="[verticalAlignment(), isFullBleed(module)]"
        >

            <Component
                :is="getComponent(module)"
                :section="moduleSection(module)"
                :key="module.moduleId"
            />

        </div>

        <PortfolioNavigationSocialLinks
            v-if="showHeroInlineSocialLinks"
            :class="['navbar-inline', inlineSocialAlignClass]"
        />
    </SectionBase>
</template>

<script>
import {updateSectionLevelColorCssInHead} from "@/scripts/common/TextStyleRenderHelper.js";
import {SectionModuleTypeEnum, SectionTypesEnum, SocialLinksPlacementEnum} from "@/scripts/common/enums.js";
import { resolveRowSpan } from "@/scripts/common/utilities/layout.js";
import Media from "@/scripts/view/portfolio/sections/modules/Media.vue";
import Text from "@/scripts/view/portfolio/sections/modules/Text.vue";
import Title from "@/scripts/view/portfolio/sections/modules/Title.vue";
import ContactForm from "@/scripts/view/portfolio/sections/modules/ContactForm.vue";
import Accordion from "@/scripts/view/portfolio/sections/modules/Accordion.vue";
import CtaTitle from "@/scripts/view/portfolio/sections/modules/CtaTitle.vue";
import PortfolioNavigationSocialLinks from "@/scripts/view/portfolio/sections/common/navigation/PortfolioNavigationSocialLinks.vue";
import SectionBase from "@/scripts/view/portfolio/sections/SectionBase.vue";
import { mapGetters } from "vuex";
import {getModuleMedia, getModuleRenderSection} from "@/scripts/common/utilities/generalSection.js";

export default {
    name: "GeneralSection",
    components: {
        SectionBase,
        Title,
        Media,
        Text,
        ContactForm,
        Accordion,
        CtaTitle,
        PortfolioNavigationSocialLinks
    },
    props:['section', 'projectTitle'],
    beforeMount() {
        updateSectionLevelColorCssInHead(this.section)
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        portfolioHeroId() {
            return this.viewData?.portfolio?.portfolioHero?._id || null;
        },
        showHeroInlineSocialLinks() {
            if (this.section?.type !== SectionTypesEnum.HERO) return false;
            if (this.portfolioHeroId && this.section?._id !== this.portfolioHeroId) return false;
            const navigation = this.$store.state.portfolio?.portfolioNavigation;
            if (!navigation) return false;
            const position = navigation?.socialLinks?.position;
            if (position !== SocialLinksPlacementEnum.INLINE_STICK_TO_BOTTOM && position !== SocialLinksPlacementEnum.OFF_GRID) return false;
            const links = this.$store.state.portfolio?.socialLinks || [];
            return Array.isArray(links) && links.length > 0;
        },
        inlineSocialAlignClass() {
            const align = this.section?.styleSettings?.boxAlign?.split('-')?.[0] || 'left';
            return `align-${align}`;
        },
    },
    methods: {
        moduleSection(module) {
            return getModuleRenderSection(this.section, module);
        },
        visibleModules() {
            return this.section.layoutSettings?.modules.filter(module => module.type !== SectionModuleTypeEnum.SPACER)
        },
        getComponent(module) {
            if(module.type === SectionModuleTypeEnum.MEDIA) return "Media"
            if(module.type === SectionModuleTypeEnum.TEXT) return "Text"
            if(module.type === SectionModuleTypeEnum.TITLE) return "Title"
            if(module.type === SectionModuleTypeEnum.CONTACT_FORM) return "ContactForm"
            if(module.type === SectionModuleTypeEnum.ACCORDION) return "Accordion"
            if(module.type === SectionModuleTypeEnum.CTA) return "CtaTitle"
        },
        calculateMediaAspectRatio(module) {
            const media = getModuleMedia(this.section, module.moduleId);
            if (module.type !== SectionModuleTypeEnum.MEDIA || media?.mockupSettings) return null;

            if (!module.gridSettings?.mediaHeight) return media?.width && media?.height ? `${media.width} / ${media.height}` : null;

            const savedAr = module.gridSettings?.mediaAspectRatio
            if(savedAr) {
                return `${savedAr[0]} / ${savedAr[1]}`
            }

            return null
        },
        columnSettings(module) {
            const { rowStart, rowEnd } = resolveRowSpan(module.gridSettings);
            return {
                '--columnStart': module.gridSettings?.columnStart,
                '--columnEnd': module.gridSettings?.columnEnd,
                '--rowStart': rowStart,
                '--rowEnd': rowEnd,
                '--height': module.gridSettings?.height ? `${module.gridSettings?.height}px` : null,
                '--mediaHeight': !this.calculateMediaAspectRatio(module) && module.gridSettings?.mediaHeight ? `${module.gridSettings?.mediaHeight}px` : null,
                '--mediaAspectRatio': this.calculateMediaAspectRatio(module),
            }
        },
        verticalAlignment() {
            return `aligned-${this.section.styleSettings && this.section.styleSettings?.boxAlign && this.section.styleSettings?.boxAlign.split('-')[1] || 'middle'}`
        },
        isFullBleed(module) {
            return {'is-fullbleed': module.type === SectionModuleTypeEnum.MEDIA && module.gridSettings?.isFullBleed}
        },
    }
}
</script>
